/**
 * EinsteinPanel — Global Floating AI Assistant
 * Accessible from every screen. Bottom-right floating chat button.
 * Context-aware, voice-enabled (Kokoro TTS + browser STT).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Brain, X, Send, Mic, MicOff, Volume2, VolumeX, Minimize2,
  Maximize2, RefreshCw, Zap, MessageCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_LABELS = {
  '/': 'Home',
  '/strategist': 'Einstein Chat',
  '/brainstorm': 'Brainstorm Lab',
  '/research': 'Research Lab',
  '/auto-strategy': 'Auto Strategy',
  '/blog': 'Blog Publisher',
  '/trending': 'Trending Topics',
  '/influencer': 'Influencer Hub',
  '/review-dashboard': 'Review Dashboard',
  '/scheduler': 'Post Scheduler',
  '/content-studio': 'Content Studio',
  '/settings': 'Settings',
  '/calendar': 'Content Calendar',
  '/intel': 'Competitive Intel',
  '/performance': 'Performance AI',
};

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2 rounded-2xl rounded-bl-sm" style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.2)', width: 'fit-content' }}>
      {[0, 1, 2].map(i => (
        <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: '#a68eff', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

function Message({ msg }) {
  const isEinstein = msg.role === 'assistant';
  return (
    <div className={`flex gap-2 ${isEinstein ? 'items-start' : 'items-start flex-row-reverse'}`}>
      {isEinstein && (
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)' }}>
          <Brain size={12} className="text-white" />
        </div>
      )}
      <div
        className="max-w-[82%] px-3 py-2 text-xs leading-relaxed rounded-2xl"
        style={{
          background: isEinstein ? 'rgba(124,94,250,0.12)' : 'rgba(61,255,160,0.1)',
          border: `1px solid ${isEinstein ? 'rgba(124,94,250,0.25)' : 'rgba(61,255,160,0.2)'}`,
          color: isEinstein ? '#e8e8f0' : '#d0fff0',
          borderTopLeftRadius: isEinstein ? 4 : undefined,
          borderTopRightRadius: isEinstein ? undefined : 4,
        }}
      >
        {msg.text}
      </div>
    </div>
  );
}

export function EinsteinPanel() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [convId] = useState(() => `einstein-float-${Date.now()}`);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [profile, setProfile] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const location = useLocation();

  const currentPage = PAGE_LABELS[location.pathname] || 'IdeaWorks';

  // Load onboarding profile for advisory context
  useEffect(() => {
    axios.get(`${API}/api/onboarding/profile`).then(({ data }) => {
      if (data.profile?.business_name) setProfile(data.profile);
    }).catch(() => {});
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimized]);

  // Pulsing indicator every 45s
  useEffect(() => {
    if (open) return;
    const interval = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 2000);
    }, 45000);
    return () => clearInterval(interval);
  }, [open]);

  // Play TTS audio
  const speak = useCallback(async (text) => {
    if (!voiceEnabled || isSpeaking) return;
    try {
      setIsSpeaking(true);
      const { data } = await axios.post(`${API}/api/voice/tts`, { text, voice: 'am_adam', speed: 0.95 });
      if (data.audio_base64) {
        const audioBytes = atob(data.audio_base64);
        const arr = new Uint8Array(audioBytes.length);
        for (let i = 0; i < audioBytes.length; i++) arr[i] = audioBytes.charCodeAt(i);
        const blob = new Blob([arr], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
        audio.onerror = () => setIsSpeaking(false);
        await audio.play();
      }
    } catch {
      setIsSpeaking(false);
    }
  }, [voiceEnabled, isSpeaking]);

  // Voice input (browser STT)
  const toggleListen = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      // Build advisory context from profile
      let advisoryCtx = '';
      if (profile) {
        advisoryCtx = ` | BRAND PROFILE: ${profile.business_name || ''} · Tone: ${profile.tone || ''} · Positioning: ${profile.positioning || ''} · Audience: ${profile.target_audience || ''} · Guardrails: ${profile.guardrails || ''}`;
      }
      const pageCtx = `The user is currently on the "${currentPage}" page of IdeaWorks.${advisoryCtx}`;
      const { data } = await axios.post(`${API}/api/strategist/chat`, {
        message: `[Context: ${pageCtx}] ${msg}`,
        conversation_id: convId,
        mode: 'sid',
        advisory_mode: true,
      });
      const resp = data.response;
      const replyText = typeof resp === 'string' ? resp : (resp?.message || JSON.stringify(resp));
      setMessages(prev => [...prev, { role: 'assistant', text: replyText }]);
      speak(replyText.slice(0, 400));
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, Einstein is momentarily offline. Please try again.' }]);
    }
    setLoading(false);
  }, [input, loading, convId, currentPage, speak, profile]);

  const reset = () => {
    setMessages([]);
    axios.delete(`${API}/api/strategist/reset/${convId}`).catch(() => {});
  };

  // Greeting on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = profile?.business_name
        ? `Fascinating! Welcome back. I see you're on the ${currentPage} page. I've loaded your brand context for ${profile.business_name} — I'm ready to advise, generate, and execute. What shall we work on?`
        : `Hi! I'm Einstein, your AI creative strategist. You're on the ${currentPage} page. I can generate content, suggest strategies, and advise on your brand voice. How can I help?`;
      setMessages([{ role: 'assistant', text: greeting }]);
    }
  }, [open]);

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="einstein-panel-trigger"
          className="fixed bottom-6 right-6 z-[10000] flex items-center justify-center transition-all hover:scale-110"
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c5efa, #a68eff)',
            boxShadow: pulse
              ? '0 0 0 8px rgba(124,94,250,0.15), 0 8px 32px rgba(124,94,250,0.5)'
              : '0 8px 32px rgba(124,94,250,0.4)',
            transition: 'box-shadow 0.4s ease, transform 0.2s ease',
          }}
        >
          <Brain size={22} className="text-white" />
          {pulse && (
            <div className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(124,94,250,0.3)' }} />
          )}
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="einstein-panel"
          className="fixed bottom-6 right-6 z-[10000] flex flex-col overflow-hidden"
          style={{
            width: minimized ? 280 : 360,
            height: minimized ? 56 : 520,
            borderRadius: 20,
            background: 'rgba(10,10,22,0.97)',
            border: '1px solid rgba(124,94,250,0.35)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(124,94,250,0.12)',
            backdropFilter: 'blur(24px)',
            transition: 'width 0.25s ease, height 0.25s ease',
            animation: 'fade-in 0.2s ease',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 flex-shrink-0"
            style={{ borderBottom: minimized ? 'none' : '1px solid rgba(124,94,250,0.2)', background: 'rgba(124,94,250,0.06)' }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', flexShrink: 0 }}>
              <Brain size={14} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black text-white">Einstein</div>
              {!minimized && (
                <div className="text-[10px] truncate" style={{ color: 'rgba(166,142,255,0.7)' }}>
                  On: {currentPage}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {!minimized && (
                <>
                  <button
                    onClick={() => setVoiceEnabled(v => !v)}
                    data-testid="einstein-voice-toggle"
                    className="p-1.5 rounded-lg transition-all hover:opacity-80"
                    style={{ background: voiceEnabled ? 'rgba(61,255,160,0.15)' : 'rgba(255,255,255,0.06)', color: voiceEnabled ? '#3dffa0' : 'rgba(255,255,255,0.4)' }}
                    title={voiceEnabled ? 'Disable voice' : 'Enable voice'}
                  >
                    {isSpeaking ? <Volume2 size={13} className="animate-pulse" /> : voiceEnabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
                  </button>
                  <button onClick={reset} className="p-1.5 rounded-lg hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }} title="Reset conversation">
                    <RefreshCw size={12} />
                  </button>
                </>
              )}
              <button onClick={() => setMinimized(m => !m)} className="p-1.5 rounded-lg hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }}>
                {minimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
              </button>
              <button onClick={() => setOpen(false)} data-testid="einstein-panel-close"
                className="p-1.5 rounded-lg hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }}>
                <X size={13} />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg, i) => <Message key={i} msg={msg} />)}
                {loading && <TypingDots />}
                <div ref={bottomRef} />
              </div>

              {/* Quick prompts */}
              {messages.length === 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                  {[
                    'Suggest content ideas for today',
                    'What should I post on LinkedIn?',
                    'Review my content strategy',
                    'Help me improve my writing',
                  ].map(q => (
                    <button key={q} onClick={() => sendMessage(q)}
                      className="text-[10px] px-2 py-1 rounded-full transition-all hover:opacity-80"
                      style={{ background: 'rgba(124,94,250,0.1)', border: '1px solid rgba(124,94,250,0.25)', color: '#a68eff' }}>
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="flex items-center gap-2 p-3 flex-shrink-0"
                style={{ borderTop: '1px solid rgba(124,94,250,0.15)' }}>
                <button onClick={toggleListen} data-testid="einstein-mic-btn"
                  className="p-2 rounded-xl flex-shrink-0 transition-all"
                  style={{
                    background: isListening ? 'rgba(255,80,80,0.2)' : 'rgba(255,255,255,0.05)',
                    color: isListening ? '#ff5050' : 'rgba(255,255,255,0.35)',
                    border: `1px solid ${isListening ? 'rgba(255,80,80,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  }}>
                  {isListening ? <MicOff size={14} className="animate-pulse" /> : <Mic size={14} />}
                </button>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Ask Einstein anything..."
                  data-testid="einstein-input"
                  className="flex-1 bg-transparent text-xs outline-none"
                  style={{ color: '#e8e8f0' }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  data-testid="einstein-send-btn"
                  className="p-2 rounded-xl flex-shrink-0 transition-all hover:opacity-80 disabled:opacity-30"
                  style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
                  {loading ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
